import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import AdminLayout from '../../components/AdminLayout'
import {
  getEntregas, marcarEnRuta, actualizarBultos, getDiaRepartoLabel, getFechaReparto,
  isAfterCutoff, CUTOFF_HOUR, ESTADO_LABEL, ESTADO_COLOR,
  getUsuariosReparto, asignarRepartidor, reagendarEntrega, getEntregaFotoSignedUrl,
  quienRecibe, quienPaga, esRegalo
} from '../../lib/entregasApi'
import { getCapacidadDiaria } from '../../lib/cobrosApi'

const btnBulto = {
  width: 24, height: 24, borderRadius: 5,
  background: '#111', color: '#fff', border: 0, cursor: 'pointer',
  fontSize: 14, fontWeight: 800, lineHeight: 1, padding: 0,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
}

export default function Entregas() {
  const [fecha, setFecha] = useState(getFechaReparto())
  const [entregas, setEntregas] = useState([])
  const [usuarios, setUsuarios] = useState([])
  const [loading, setLoading] = useState(true)
  const [seleccionadas, setSeleccionadas] = useState(new Set())
  const [enviando, setEnviando] = useState(false)
  const [filtroRep, setFiltroRep] = useState('todos')  // todos | sin_asignar | <userId>
  const [capacidad, setCapacidad] = useState(20)
  const navigate = useNavigate()

  useEffect(() => { load() }, [fecha])
  useEffect(() => {
    getUsuariosReparto().then(setUsuarios)
    getCapacidadDiaria().then(setCapacidad)
  }, [])

  async function load() {
    setLoading(true)
    const data = await getEntregas(fecha)
    setEntregas(data)
    setLoading(false)
  }

  async function cambiarAsignado(entregaId, repartidorId) {
    const v = repartidorId || null
    setEntregas(es => es.map(e => e.id === entregaId ? {
      ...e,
      repartidor_id: v,
      repartidor: v ? (usuarios.find(u => u.id === v) ? { nombre: usuarios.find(u => u.id === v).nombre, role: usuarios.find(u => u.id === v).role } : null) : null
    } : e))
    try { await asignarRepartidor(entregaId, v) } catch (err) { console.error(err); load() }
  }

  async function asignarSeleccionadasA(repartidorId) {
    if (seleccionadas.size === 0) { alert('Seleccioná entregas primero.'); return }
    if (!confirm(`Asignar ${seleccionadas.size} entrega${seleccionadas.size !== 1 ? 's' : ''} a este repartidor?`)) return
    try {
      await asignarRepartidor([...seleccionadas], repartidorId || null)
      await load()
    } catch (err) { alert('Error: ' + err.message) }
  }

  function todayStr() {
    return new Date().toISOString().slice(0, 10)
  }
  function tomorrowStr() {
    const d = new Date()
    d.setDate(d.getDate() + 1)
    return d.toISOString().slice(0, 10)
  }

  function toggle(id) {
    const n = new Set(seleccionadas)
    n.has(id) ? n.delete(id) : n.add(id)
    setSeleccionadas(n)
  }

  function toggleAll() {
    const seleccionables = entregas.filter(e => e.estado !== 'cancelada').map(e => e.id)
    if (seleccionadas.size === seleccionables.length) setSeleccionadas(new Set())
    else setSeleccionadas(new Set(seleccionables))
  }

  async function salirARuta() {
    // Solo afecta programadas, aunque haya otras seleccionadas
    const programadasSel = entregas
      .filter(e => seleccionadas.has(e.id) && e.estado === 'programada')
      .map(e => e.id)
    if (programadasSel.length === 0) {
      alert('No hay entregas programadas seleccionadas.')
      return
    }
    if (!confirm(`Marcar ${programadasSel.length} entregas como "en ruta" y notificar a los clientes por WhatsApp?`)) return
    setEnviando(true)
    try {
      await marcarEnRuta(programadasSel)
      await load()
      setSeleccionadas(new Set())
      alert('Listo. WhatsApp enviado a los clientes.')
    } catch (e) {
      alert('Error: ' + e.message)
    } finally {
      setEnviando(false)
    }
  }

  function imprimirEtiquetas() {
    if (seleccionadas.size === 0) {
      alert('Seleccioná al menos una entrega para imprimir su etiqueta.')
      return
    }
    navigate(`/admin/etiquetas?ids=${[...seleccionadas].join(',')}`)
  }

  function hojaArmado() {
    if (seleccionadas.size === 0) {
      alert('Seleccioná al menos una entrega para generar la hoja de armado.')
      return
    }
    navigate(`/admin/armado?ids=${[...seleccionadas].join(',')}`)
  }

  async function cambiarBultos(entregaId, nuevo) {
    const n = Math.max(1, Math.min(20, nuevo))
    // optimistic update
    setEntregas(es => es.map(e => e.id === entregaId ? { ...e, bultos_total: n } : e))
    try { await actualizarBultos(entregaId, n) } catch (err) {
      console.error('actualizarBultos:', err)
      load() // revertir si falla
    }
  }

  const corteInfo = useMemo(() => {
    const after = isAfterCutoff()
    const dia = getDiaRepartoLabel()
    if (after) return { tipo: 'err', msg: `🔒 CORTE CERRADO · entregas de mañana (${dia}) ya fijadas` }
    const restantes = CUTOFF_HOUR - new Date().getHours()
    return { tipo: 'info', msg: `Faltan ${restantes}h para el corte de las ${CUTOFF_HOUR}hs · viendo entregas de hoy (${dia})` }
  }, [fecha])

  const programadasCount = entregas.filter(e => e.estado === 'programada').length
  const enRutaCount = entregas.filter(e => e.estado === 'en_ruta').length
  const entregadasCount = entregas.filter(e => e.estado === 'entregada').length
  const selProgramadasCount = entregas.filter(e => seleccionadas.has(e.id) && e.estado === 'programada').length
  const sinAsignarCount = entregas.filter(e => !e.repartidor_id && e.estado !== 'cancelada').length

  const entregasFiltradas = useMemo(() => {
    if (filtroRep === 'todos') return entregas
    if (filtroRep === 'sin_asignar') return entregas.filter(e => !e.repartidor_id)
    return entregas.filter(e => e.repartidor_id === filtroRep)
  }, [entregas, filtroRep])

  return (
    <AdminLayout>
      <div className="page">
        <h1>Entregas</h1>
        <p className="lead">Lista del día con corte 16hs · Imprimir etiquetas · Salir a ruta</p>
        <div className={`banner ${corteInfo.tipo}`}>
          🕐 <span>{corteInfo.msg}</span>
        </div>

        <div className="cards-grid" style={{ marginBottom: 20 }}>
          <div className="card">
            <h3>Para fecha</h3>
            <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
              <button
                className={`btn ghost ${fecha === todayStr() ? 'active' : ''}`}
                style={{ padding: '6px 12px', fontSize: 12, ...(fecha === todayStr() ? { background: 'var(--navy)', color: '#fff', borderColor: 'var(--navy)' } : {}) }}
                onClick={() => setFecha(todayStr())}>Hoy</button>
              <button
                className={`btn ghost ${fecha === tomorrowStr() ? 'active' : ''}`}
                style={{ padding: '6px 12px', fontSize: 12, ...(fecha === tomorrowStr() ? { background: 'var(--navy)', color: '#fff', borderColor: 'var(--navy)' } : {}) }}
                onClick={() => setFecha(tomorrowStr())}>Mañana</button>
            </div>
            <input type="date" value={fecha} onChange={e => setFecha(e.target.value)} className="date-input" />
          </div>
          <div className="card">
            <h3>Programadas</h3>
            <div className="stat" style={{ color: '#0EA5E9' }}>{programadasCount}</div>
          </div>
          <div className="card">
            <h3>En ruta</h3>
            <div className="stat" style={{ color: '#F59E0B' }}>{enRutaCount}</div>
          </div>
          <div className="card">
            <h3>Entregadas</h3>
            <div className="stat" style={{ color: '#10B981' }}>{entregadasCount}</div>
          </div>
          <div className="card" style={{
            borderLeft: `4px solid ${entregas.length >= capacidad ? '#EF4444' : entregas.length >= capacidad * 0.8 ? '#F59E0B' : '#10B981'}`,
          }}>
            <h3>Capacidad</h3>
            <div className="stat" style={{ color: entregas.length >= capacidad ? '#EF4444' : entregas.length >= capacidad * 0.8 ? '#F59E0B' : '#10B981' }}>
              {entregas.length} / {capacidad}
            </div>
            <div className="desc">
              {entregas.length >= capacidad
                ? '⚠️ Día lleno'
                : entregas.length >= capacidad * 0.8
                  ? `🟡 Quedan ${capacidad - entregas.length} cupos`
                  : `${capacidad - entregas.length} cupos disponibles`}
            </div>
          </div>
        </div>

        <div className="btn-row" style={{ flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <button
            className="btn primary"
            onClick={salirARuta}
            disabled={selProgramadasCount === 0 || enviando}
            title="Solo actúa sobre las seleccionadas que están en estado Programada"
          >
            🚐 Salir a ruta ({selProgramadasCount})
          </button>
          <button className="btn ghost" onClick={hojaArmado} disabled={seleccionadas.size === 0}>
            📋 Hoja de armado ({seleccionadas.size})
          </button>
          <button className="btn ghost" onClick={imprimirEtiquetas} disabled={seleccionadas.size === 0}>
            🖨️ Imprimir etiquetas ({seleccionadas.size})
          </button>
          {seleccionadas.size > 0 && usuarios.length > 0 && (
            <select
              className="date-input"
              style={{ padding: '6px 10px', fontSize: 13 }}
              defaultValue=""
              onChange={e => {
                const v = e.target.value
                if (!v) return
                asignarSeleccionadasA(v === '__null__' ? null : v)
                e.target.value = ''
              }}
              title="Asignar a repartidor en bloque"
            >
              <option value="">👤 Asignar a… ({seleccionadas.size})</option>
              <option value="__null__">— Quitar asignación —</option>
              {usuarios.map(u => (
                <option key={u.id} value={u.id}>{u.nombre} {u.role === 'admin' ? '(admin)' : ''}</option>
              ))}
            </select>
          )}
          <button className="btn ghost" onClick={load}>↺ Recargar</button>
        </div>

        {/* Filtro por repartidor */}
        <div className="btn-row" style={{ marginTop: 12, gap: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: 'var(--muted)', marginRight: 4 }}>Ver:</span>
          <button
            className={`btn ghost ${filtroRep === 'todos' ? 'active' : ''}`}
            style={{ padding: '4px 10px', fontSize: 12, ...(filtroRep === 'todos' ? { background: 'var(--navy)', color: '#fff', borderColor: 'var(--navy)' } : {}) }}
            onClick={() => setFiltroRep('todos')}
          >Todos ({entregas.length})</button>
          <button
            className={`btn ghost ${filtroRep === 'sin_asignar' ? 'active' : ''}`}
            style={{ padding: '4px 10px', fontSize: 12, ...(filtroRep === 'sin_asignar' ? { background: '#92400E', color: '#fff', borderColor: '#92400E' } : {}) }}
            onClick={() => setFiltroRep('sin_asignar')}
          >Sin asignar ({sinAsignarCount})</button>
          {usuarios.map(u => {
            const count = entregas.filter(e => e.repartidor_id === u.id).length
            return (
              <button
                key={u.id}
                className={`btn ghost ${filtroRep === u.id ? 'active' : ''}`}
                style={{ padding: '4px 10px', fontSize: 12, ...(filtroRep === u.id ? { background: 'var(--navy)', color: '#fff', borderColor: 'var(--navy)' } : {}) }}
                onClick={() => setFiltroRep(u.id)}
              >{u.nombre} ({count})</button>
            )
          })}
        </div>

        {loading ? (
          <div className="card" style={{ textAlign: 'center', color: 'var(--muted)' }}>Cargando...</div>
        ) : entregas.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', color: 'var(--muted)' }}>
            No hay entregas programadas para esta fecha.
          </div>
        ) : (
          <table className="t-table">
            <thead>
              <tr>
                <th style={{ width: 36 }}>
                  <input
                    type="checkbox"
                    checked={seleccionadas.size > 0 && seleccionadas.size === programadasCount}
                    onChange={toggleAll}
                  />
                </th>
                <th>#</th>
                <th>Cliente</th>
                <th>Barrio · Dirección</th>
                <th>Kit</th>
                <th>Ventana</th>
                <th style={{ textAlign: 'center' }}>Bultos</th>
                <th>Asignado a</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {entregasFiltradas.map(e => {
                const c = ESTADO_COLOR[e.estado] || ESTADO_COLOR.programada
                const tieneExtras = (e.productos_extras || []).length > 0
                return (
                  <tr key={e.id}>
                    <td>
                      {e.estado !== 'cancelada' && (
                        <input
                          type="checkbox"
                          checked={seleccionadas.has(e.id)}
                          onChange={() => toggle(e.id)}
                        />
                      )}
                    </td>
                    <td className="num" style={{ fontFamily: 'monospace', fontSize: 12 }}>{e.numero_orden}</td>
                    <td>
                      <div style={{ fontWeight: 700 }}>{quienRecibe(e.cliente)?.nombre || '—'}</div>
                      <div style={{ fontSize: 12, color: 'var(--muted)' }}>{quienRecibe(e.cliente)?.phone || '—'}</div>
                      {esRegalo(e.cliente) && (
                        <div style={{ fontSize: 10, color: '#7C3AED', marginTop: 2, fontWeight: 700 }}>
                          🎁 paga: {quienPaga(e.cliente)?.nombre}
                        </div>
                      )}
                    </td>
                    <td>
                      {e.cliente?.alias && <div style={{ fontSize: 11, fontWeight: 800, color: '#0F1F3D', textTransform: 'uppercase', letterSpacing: '.05em' }}>{e.cliente.alias}</div>}
                      <div>{e.cliente?.barrio}</div>
                      <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                        {e.cliente?.direccion}{e.cliente?.apto ? ` · ${e.cliente.apto}` : ''}
                      </div>
                    </td>
                    <td>
                      {e.kit?.nombre || '—'}
                      {tieneExtras && <span style={{ color: 'var(--teal)', fontSize: 11, marginLeft: 6 }}>+ extras</span>}
                    </td>
                    <td style={{ fontSize: 12 }}>
                      {e.ventana_horaria === 'manana' ? '🌅 10–14hs' : '🌇 14–18hs'}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <button
                          onClick={() => cambiarBultos(e.id, (e.bultos_total || 1) - 1)}
                          disabled={(e.bultos_total || 1) <= 1 || e.estado === 'cancelada'}
                          title="Quitar bulto"
                          style={btnBulto}
                        >−</button>
                        <span style={{ minWidth: 22, fontWeight: 800, fontSize: 14, fontVariantNumeric: 'tabular-nums' }}>
                          {e.bultos_total || 1}
                        </span>
                        <button
                          onClick={() => cambiarBultos(e.id, (e.bultos_total || 1) + 1)}
                          disabled={e.estado === 'cancelada'}
                          title="Agregar bulto"
                          style={btnBulto}
                        >+</button>
                      </div>
                    </td>
                    <td>
                      <select
                        value={e.repartidor_id || ''}
                        onChange={ev => cambiarAsignado(e.id, ev.target.value || null)}
                        disabled={e.estado === 'cancelada' || e.estado === 'entregada'}
                        style={{
                          padding: '4px 6px', fontSize: 12,
                          borderRadius: 6,
                          border: e.repartidor_id ? '1px solid #ccc' : '1.5px solid #F59E0B',
                          background: e.repartidor_id ? '#fff' : '#FEF3C7',
                          fontWeight: 600,
                          cursor: 'pointer',
                          maxWidth: 130,
                        }}
                      >
                        <option value="">— sin asignar —</option>
                        {usuarios.map(u => (
                          <option key={u.id} value={u.id}>{u.nombre}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <span style={{ background: c.bg, color: c.text, padding: '4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em' }}>
                        ● {ESTADO_LABEL[e.estado]}
                      </span>
                      {e.estado === 'no_entregada' && (
                        <NoEntregaCell entrega={e} onReagendado={load} />
                      )}
                      {e.estado === 'entregada' && e.foto_url && (
                        <VerFotoBtn path={e.foto_url} />
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </AdminLayout>
  )
}

/* Detalle de visita fallida en la tabla admin: motivo + Ver foto + Reagendar */
function NoEntregaCell({ entrega, onReagendado }) {
  async function reagendar() {
    const sugerido = nextDateStr(entrega.fecha_programada)
    const fecha = window.prompt(
      `Reagendar entrega para qué fecha? (YYYY-MM-DD)\nMotivo original: ${entrega.motivo_no_entrega || '—'}`,
      sugerido
    )
    if (!fecha) return
    if (!/^\d{4}-\d{2}-\d{2}$/.test(fecha)) { alert('Fecha inválida (formato YYYY-MM-DD)'); return }
    try {
      await reagendarEntrega(entrega.id, fecha)
      onReagendado()
    } catch (e) { alert('Error al reagendar: ' + e.message) }
  }
  return (
    <div style={{ marginTop: 6, fontSize: 11, lineHeight: 1.4 }}>
      <div style={{ color: '#991B1B', fontWeight: 600 }}>
        {entrega.motivo_no_entrega || 'Sin motivo'}
      </div>
      <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
        {entrega.foto_url && <VerFotoBtn path={entrega.foto_url} />}
        <button
          type="button"
          onClick={reagendar}
          style={{ background: '#1A3461', color: '#fff', border: 0, padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
        >
          🔄 Reagendar
        </button>
      </div>
    </div>
  )
}

/* Botón inline que abre signed URL de foto en nueva pestaña */
function VerFotoBtn({ path }) {
  async function abrir() {
    const url = await getEntregaFotoSignedUrl(path)
    if (!url) { alert('No se pudo cargar la foto'); return }
    window.open(url, '_blank', 'noopener')
  }
  return (
    <button
      type="button"
      onClick={abrir}
      style={{ background: '#fff', color: '#1A3461', border: '1px solid #1A3461', padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
    >
      📷 Ver foto
    </button>
  )
}

function nextDateStr(fechaIso) {
  try {
    const d = new Date((fechaIso || todayIso()) + 'T12:00:00')
    d.setDate(d.getDate() + 1)
    return d.toISOString().slice(0, 10)
  } catch { return todayIso() }
}
function todayIso() {
  return new Date().toISOString().slice(0, 10)
}
